import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// template_submissions table; TemplateSubmissionService.ToDto in lureo-backend Data/Api/V1/Templates/TemplateSubmissionService.cs
export type TemplateSubmissionDto = components['schemas']['TemplateSubmissionDto'];
export type TemplateSubmissionStatus = components['schemas']['TemplateSubmissionStatus'];
export type SubmitStoryAsTemplateRequest = components['schemas']['SubmitStoryAsTemplateRequest'];
export type TemplateSubmitResultDto = components['schemas']['TemplateSubmitResultDto'];

const spec = {
  uniqueId: 'template_submission_id',
  sceneUniqueId: 'scene_instance_id',
  sceneOriginUniqueId: 'scene_origin_id',
  userUniqueId: 'user_id',
  // Stored lowercase, which is the v1 value
  status: 'status',
  submittedAt: 'submitted_at',
  reviewedAt: 'reviewed_at',
  // Null for everyone but staff
  reviewedByUserUniqueId: 'reviewed_by',
  rejectionReason: 'rejection_reason',
  rating: 'rating',
  shouldBePaid: 'should_be_paid',
  metBonusCampaign: 'met_bonus_campaign',
  paidOut: 'paid_out',
  paidAt: 'paid_at',
  // Null for everyone but staff
  paidByUserUniqueId: 'paid_by',
  // slack or dashboard, stored lowercase; any other stored value reads as null
  approvalSource: 'approval_source',
  // Null for everyone but staff
  reviewerNotes: 'reviewer_notes',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
} as const satisfies FieldSpec<TemplateSubmissionDto>;

const map = fieldMap<TemplateSubmissionDto, typeof spec>('Template submission', spec);

export type LegacyTemplateSubmission = LegacyRow<typeof spec, TemplateSubmissionDto>;

export const templateSubmissionToLegacy = (dto: TemplateSubmissionDto): LegacyTemplateSubmission => map.toLegacy(dto);

/**
 * submitStoryAsTemplate body (POST /api/v1/template-submissions/from-story), from the editor's
 * submit parameters. It replaces the whole client-side sequence: the copy into the published
 * templates project, its name, listing and gallery media, the submission or resubmission, and
 * retiring the previous copy. `templateSceneId` is a new client id for the copy; repeating it
 * returns the first result. The story, owner, plan and email come from the scene and the bearer.
 */
export function submitStoryAsTemplateBody(params: {
  templateSceneId: string;
  sceneInstanceId: string;
  storyName?: string | null;
  description?: string | null;
  learnings?: string | null;
  category?: string | null;
  difficulty?: string | null;
  tags?: string[] | null;
  features?: string[] | null;
}): SubmitStoryAsTemplateRequest {
  return {
    uniqueId: requireId('Template submit', 'templateSceneId', params.templateSceneId),
    sceneUniqueId: requireId('Template submit', 'sceneInstanceId', params.sceneInstanceId),
    name: params.storyName,
    description: params.description,
    learnings: params.learnings,
    category: params.category,
    difficulty: params.difficulty,
    tags: params.tags,
    features: params.features,
  };
}

/** The editor's submit result from submitStoryAsTemplate's answer, with the submission as a legacy row. */
export function templateSubmitResult(dto: TemplateSubmitResultDto) {
  return {
    templateSceneInstanceId: dto.templateSceneUniqueId,
    templateSceneOriginId: dto.templateSceneOriginUniqueId,
    templateSubmissionId: dto.templateSubmissionUniqueId,
    isResubmission: dto.isResubmission,
    submission: templateSubmissionToLegacy(dto.submission),
  };
}

